import React, { useState, useEffect } from 'react';
import axios from 'axios';

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'TBD';

const EMPTY_FORM = {
  project_id: '', building: '', milestone_name: '', milestone_type: 'level',
  description: '', target_date: '', completion_percentage: 0, status: 'planned', notes: ''
};

const ConstructionMilestones = ({ apiBase }) => {
  const [milestones, setMilestones] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [imageFiles, setImageFiles] = useState([]);
  const [videoFiles, setVideoFiles] = useState([]);
  const [pdfFiles, setPdfFiles] = useState([]);
  const [reelUrl, setReelUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => { fetchProjects(); }, []);
  useEffect(() => { if (selectedProject) fetchMilestones(); }, [selectedProject, filterStatus]);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${apiBase}/projects`);
      setProjects(res.data);
      if (res.data.length > 0) {
        setSelectedProject(res.data[0].id.toString());
        setFormData(prev => ({ ...prev, project_id: res.data[0].id.toString() }));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchMilestones = async () => {
    try {
      const res = await axios.get(`${apiBase}/construction-milestones/project/${selectedProject}`);
      let data = res.data || [];
      if (filterStatus) data = data.filter(m => m.status === filterStatus);
      setMilestones(data);
    } catch (e) { console.error(e); }
  };

  const openNew = () => {
    setEditingMilestone(null);
    setFormData({ ...EMPTY_FORM, project_id: selectedProject });
    setImageFiles([]); setVideoFiles([]); setPdfFiles([]); setReelUrl('');
    setShowModal(true);
  };

  const handleEdit = (m) => {
    setEditingMilestone(m);
    setFormData({
      project_id: m.project_id || selectedProject, building: m.building || '',
      milestone_name: m.milestone_name, milestone_type: m.milestone_type || 'level',
      description: m.description || '', target_date: m.target_date ? m.target_date.split('T')[0] : '',
      completion_percentage: m.completion_percentage || 0, status: m.status || 'planned', notes: m.notes || ''
    });
    setImageFiles([]); setVideoFiles([]); setPdfFiles([]);
    setReelUrl(m.reel_urls ? (typeof m.reel_urls === 'string' ? JSON.parse(m.reel_urls) : m.reel_urls)[0] || '' : '');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.keys(formData).forEach(k => fd.append(k, formData[k]));
    imageFiles.forEach(f => fd.append('images', f));
    videoFiles.forEach(f => fd.append('videos', f));
    pdfFiles.forEach(f => fd.append('pdfs', f));
    if (reelUrl) fd.append('reel_url', reelUrl);

    try {
      if (editingMilestone) {
        await axios.put(`${apiBase}/construction-milestones/${editingMilestone.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await axios.post(`${apiBase}/construction-milestones`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setShowModal(false); setEditingMilestone(null); fetchMilestones();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this milestone? Cannot be undone.')) return;
    try { await axios.delete(`${apiBase}/construction-milestones/${id}`); fetchMilestones(); }
    catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const getStatusStyle = (status) => {
    const styles = {
      planned: { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1', icon: '📋' },
      in_progress: { bg: '#fffbeb', color: '#b45309', border: '#fbbf24', icon: '🏗️' },
      completed: { bg: '#ecfdf5', color: '#047857', border: '#34d399', icon: '✅' },
      pending: { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1', icon: '⏳' },
    };
    return styles[status] || styles.planned;
  };

  if (loading) return <div className="loading">Loading...</div>;

  const stats = {
    total: milestones.length,
    completed: milestones.filter(m => m.status === 'completed').length,
    inProgress: milestones.filter(m => m.status === 'in_progress').length,
    planned: milestones.filter(m => m.status === 'planned' || m.status === 'pending').length,
  };
  const overallProgress = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px' }}>🏗️ Construction Milestones</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0' }}>Track construction progress, upload photos, videos & documents</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select className="form-input" style={{ width: 'auto', padding: '6px 10px' }} value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select className="form-input" style={{ width: 'auto', padding: '6px 10px' }} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            <option value="planned">Planned</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
          <button className="btn btn-primary" onClick={openNew}>+ Add Milestone</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <div style={{ background: '#eff6ff', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #bfdbfe' }}>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#2563eb' }}>{stats.total}</div>
          <div style={{ fontSize: '12px', color: '#2563eb', fontWeight: 600 }}>Total</div>
        </div>
        <div style={{ background: '#ecfdf5', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #a7f3d0' }}>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#059669' }}>{stats.completed}</div>
          <div style={{ fontSize: '12px', color: '#059669', fontWeight: 600 }}>Completed</div>
        </div>
        <div style={{ background: '#fffbeb', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #fde68a' }}>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#d97706' }}>{stats.inProgress}</div>
          <div style={{ fontSize: '12px', color: '#d97706', fontWeight: 600 }}>In Progress</div>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: '28px', fontWeight: 800, color: 'white' }}>{overallProgress}%</div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Overall</div>
        </div>
      </div>

      {/* Timeline */}
      <div className="card">
        <div style={{ position: 'relative' }}>
          {milestones.length > 0 ? milestones.map((m, idx) => {
            const s = getStatusStyle(m.status);
            const imageList = m.images ? m.images.split(',').filter(Boolean) : [];
            return (
              <div key={m.id} style={{ display: 'flex', gap: '20px', marginBottom: '24px', position: 'relative' }}>
                {/* Timeline Line */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '40px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: s.bg, border: `3px solid ${s.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', zIndex: 1 }}>
                    {s.icon}
                  </div>
                  {idx < milestones.length - 1 && <div style={{ width: '3px', flexGrow: 1, background: '#e2e8f0', marginTop: '4px' }} />}
                </div>

                {/* Card */}
                <div style={{ flex: 1, background: 'white', borderRadius: '12px', border: `1px solid ${s.border}`, padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 4px', fontSize: '16px' }}>{m.milestone_name}</h3>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
                          {(m.status || 'planned').replace('_', ' ').toUpperCase()}
                        </span>
                        <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', background: '#f1f5f9', color: '#475569' }}>
                          {m.milestone_type || 'general'}
                        </span>
                        {m.building && <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', background: '#eff6ff', color: '#2563eb' }}>🏢 {m.building}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button onClick={() => handleEdit(m)} style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#f8fafc', cursor: 'pointer', fontSize: '12px' }}>✏️</button>
                      <button onClick={() => handleDelete(m.id)} style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '12px' }}>🗑️</button>
                    </div>
                  </div>

                  {m.description && <p style={{ fontSize: '13px', color: '#64748b', margin: '8px 0' }}>{m.description}</p>}

                  {/* Progress Bar */}
                  {(parseInt(m.completion_percentage) || 0) > 0 && (
                    <div style={{ margin: '10px 0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                        <span style={{ color: '#64748b' }}>Progress</span>
                        <span style={{ fontWeight: 700, color: parseInt(m.completion_percentage) === 100 ? '#059669' : '#2563eb' }}>{m.completion_percentage}%</span>
                      </div>
                      <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${m.completion_percentage}%`, height: '100%', borderRadius: '4px', background: parseInt(m.completion_percentage) === 100 ? '#10b981' : 'linear-gradient(90deg, #3b82f6, #6366f1)', transition: 'width 0.5s ease' }} />
                      </div>
                    </div>
                  )}

                  {/* Date & Notes */}
                  <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#64748b', marginTop: '8px', flexWrap: 'wrap' }}>
                    <span>📅 Target: {formatDate(m.target_date)}</span>
                    {m.completed_date && <span>✅ Completed: {formatDate(m.completed_date)}</span>}
                    {m.notes && <span>📝 {m.notes}</span>}
                  </div>

                  {/* Media Gallery */}
                  {imageList.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                      {imageList.map((img, i) => (
                        <a key={i} href={`${apiBase.replace('/api', '')}/uploads/milestones/${img.trim()}`} target="_blank" rel="noopener noreferrer">
                          <img src={`${apiBase.replace('/api', '')}/uploads/milestones/${img.trim()}`} alt={`${m.milestone_name} ${i + 1}`}
                            style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #e2e8f0', cursor: 'pointer' }}
                            onError={(e) => { e.target.style.display = 'none'; }} />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          }) : (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏗️</div>
              <p style={{ fontSize: '16px' }}>No milestones yet. Click "+ Add Milestone" to start tracking construction progress.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingMilestone(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '650px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3 className="modal-title">{editingMilestone ? 'Edit Milestone' : 'Add Milestone'}</h3>
              <button className="modal-close" onClick={() => { setShowModal(false); setEditingMilestone(null); }}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Milestone Name *</label>
                <input type="text" className="form-input" required value={formData.milestone_name} onChange={(e) => setFormData({ ...formData, milestone_name: e.target.value })} placeholder="e.g., Level 5 Slab Completed" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Building / Wing</label>
                  <input type="text" className="form-input" value={formData.building} onChange={(e) => setFormData({ ...formData, building: e.target.value })} placeholder="A, B, All" />
                </div>
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="form-input" value={formData.milestone_type} onChange={(e) => setFormData({ ...formData, milestone_type: e.target.value })}>
                    <option value="level">Level / Floor</option>
                    <option value="foundation">Foundation</option>
                    <option value="structure">Structure</option>
                    <option value="plumbing">Plumbing</option>
                    <option value="electrical">Electrical</option>
                    <option value="finishing">Finishing</option>
                    <option value="oc">OC / Completion</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Target Date</label>
                  <input type="date" className="form-input" value={formData.target_date} onChange={(e) => setFormData({ ...formData, target_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Completion %</label>
                  <input type="number" className="form-input" min="0" max="100" value={formData.completion_percentage} onChange={(e) => setFormData({ ...formData, completion_percentage: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-input" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                    <option value="planned">📋 Planned</option>
                    <option value="in_progress">🏗️ In Progress</option>
                    <option value="completed">✅ Completed</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '8px' }}>
                <label className="form-label">Description</label>
                <textarea className="form-input" rows="2" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Details about this milestone..." />
              </div>

              {/* Media Uploads */}
              <div style={{ marginTop: '12px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#334155' }}>📎 Media Attachments</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">📷 Photos</label>
                    <input type="file" className="form-input" accept="image/*" multiple onChange={(e) => setImageFiles(Array.from(e.target.files))} />
                    <small style={{ color: '#94a3b8', fontSize: '11px' }}>JPG, PNG (max 10)</small>
                  </div>
                  <div className="form-group">
                    <label className="form-label">🎬 Videos</label>
                    <input type="file" className="form-input" accept="video/*" multiple onChange={(e) => setVideoFiles(Array.from(e.target.files))} />
                    <small style={{ color: '#94a3b8', fontSize: '11px' }}>MP4, MOV (max 100MB each)</small>
                  </div>
                  <div className="form-group">
                    <label className="form-label">📄 PDFs / Documents</label>
                    <input type="file" className="form-input" accept=".pdf" multiple onChange={(e) => setPdfFiles(Array.from(e.target.files))} />
                    <small style={{ color: '#94a3b8', fontSize: '11px' }}>PDF reports, drawings</small>
                  </div>
                  <div className="form-group">
                    <label className="form-label">🎥 Reel / Social Link</label>
                    <input type="url" className="form-input" value={reelUrl} onChange={(e) => setReelUrl(e.target.value)} placeholder="Instagram/YouTube reel URL" />
                    <small style={{ color: '#94a3b8', fontSize: '11px' }}>Paste link to reel</small>
                  </div>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '12px' }}>
                <label className="form-label">Notes</label>
                <textarea className="form-input" rows="2" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>

              {formData.status === 'completed' && (
                <div style={{ marginTop: '12px', padding: '12px', background: '#eff6ff', borderRadius: '6px', fontSize: '13px', color: '#1e40af' }}>
                  ℹ️ Marking as completed will notify all booked clients via WhatsApp with milestone details and images.
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{editingMilestone ? 'Update' : 'Create'} Milestone</button>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setEditingMilestone(null); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConstructionMilestones;
